import { useCallback, useEffect, useState } from "react";
import { useT } from "@transifex/react";
import { useHistory } from "react-router-dom";
import exportFromJSON from "export-from-json";
import copy from "copy-to-clipboard";
import { utils, writeFile } from "xlsx";

import { useTestReviewContext } from "../../../context/TestReviewContext";
import TestReviewInfoHeader from "../../../basic/testReviewInfoHeader/TestReviewInfoHeader";
import ContentWithHeader from "../../../wrapper/ContentWithHeader/ContentWithHeader";
import { useLogger } from "../../../hooks/useLogger";
import useBreakpoints, { Breakpoint } from "../../../hooks/useBreakpoints";
import { saveStudentTestResult } from "../../../networking/tests";

import ShareStatusBanner from "../../../complex/shareTestReviews/ShareStatusBanner";
import StudentResultCard from "../../../complex/shareTestReviews/StudentResultCard";
import ShareConfirmationPopup, {
    ShareConfirmationKinds,
} from "../../../complex/shareTestReviews/ShareConfirmationPopup";
import DownloadFormatPopup from "../../../complex/shareTestReviews/DownloadFormatPopup";
import { ORANGE_OUTLINE_BUTTON_CLASS } from "../../../complex/shareTestReviews/styleConstants";
import BackNavigationButton from "../../../basic/backNavigationButton/BackNavigationButton";
import { AreaButton, ButtonVariant, IconPosition } from "../../../complex/teacherArea/AreaButton";
import { DownloadSimpleIcon } from "@phosphor-icons/react";
import useToast from "../../../hooks/useToast";
import ToastMessage from "../../../basic/toastMessage/ToastMessage";
import { ToastVariants } from "../../../enums/toast";
import { getIsLegacyTest } from "../../../helpers/LegacyTest";

const CONTENT_MAX_WIDTH = "max-w-[1200px]";

enum DownloadFormats {
    CSV = "CSV",
    XLSX = "XLSX",
}

interface PopupState {
    kind: ShareConfirmationKinds | null;
    resultId?: string;
    studentName?: string;
}

interface ExportRow {
    [key: string]: string;
}

const ShareTestReviews = () => {
    const t = useT();
    const { logEvent } = useLogger();
    const history = useHistory();
    const { getStyleBasedOnBreakpoint } = useBreakpoints();

    const {
        generateDataForReviewSharing,
        testShareData,
        setTestShareData,
        testIndividualResults,
        setTestIndividualResults,
        currentTestId,
        studentTestContent,
    } = useTestReviewContext();

    const [popupState, setPopupState] = useState<PopupState>({ kind: null });
    const [isDownloadPopupOpen, setIsDownloadPopupOpen] = useState(false);
    const [isSharing, setIsSharing] = useState(false);
    const [visitEventSent, setVisitEventSent] = useState(false);
    const linkCopiedToast = useToast();
    const shareErrorToast = useToast();
    const downloadErrorToast = useToast();
    const prepareErrorToast = useToast();

    const t_download = t("Download", { _tags: "ShareTestReviews" });
    const t_sectionHeader = t("Evaluations", { _tags: "ShareTestReviews" });
    const t_backToOverview = t("To the evaluation overview", { _tags: "ShareTestReviews" });

    const t_exportName = t("Name", { _tags: "ShareTestReviews" });
    const t_exportCorrectPer = t("Percentage correct answers", { _tags: "ShareTestReviews" });
    const t_exportIncorrectPer = t("Percentage incorrect answers", { _tags: "ShareTestReviews" });
    const t_exportLink = t("Unique result access link", { _tags: "ShareTestReviews" });
    const t_noLink = t("No results link available", { _tags: "ShareTestReviews" });
    const t_linkCopied = t("Link copied to clipboard.", { _tags: "ShareTestReviews" });
    const t_shareFailed = t("Sharing failed. Please try again.", { _tags: "ShareTestReviews" });
    const t_downloadFailed = t("Download failed. Please try again.", { _tags: "ShareTestReviews" });
    const t_prepareFailed = t("Could not prepare the results for sharing. Please reload the page.", {
        _tags: "ShareTestReviews",
    });

    useEffect(() => {
        const prepareReviewSharing = async () => {
            const changed = await generateDataForReviewSharing();
            const toSave = (changed ?? []).filter((entry) => entry.id);
            if (toSave.length === 0) return;
            const results = await Promise.allSettled(
                toSave.map((entry) => saveStudentTestResult(currentTestId, entry.id!, entry))
            );
            const hasFailure = results.some((r) => r.status === "rejected");
            results.forEach((r, i) => {
                if (r.status === "rejected") {
                    console.warn("Failed to save share data for", toSave[i].id, r.reason);
                }
            });
            // A failed save means a freshly-created share record never persisted, so the next visit would
            // create a new one and orphan this one. Tell the teacher instead of failing silently.
            if (hasFailure) {
                prepareErrorToast.triggerToast();
            }
        };
        prepareReviewSharing();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (testShareData.length === 0 || visitEventSent) return;
        const fullyRatedPersons = testShareData.filter((s) => s.isFullyEvaluated).length;
        const totalPersonsInTest = testShareData.length;
        const fullyRatedPersonsPercent = (fullyRatedPersons * 100) / totalPersonsInTest || 0;

        logEvent("Visited Share Results Page", {
            totalPersonsInTest,
            fullyRatedPersons,
            testId: studentTestContent.id,
            fullyRatedPersonsPercent,
        });

        setVisitEventSent(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [testShareData, visitEventSent, studentTestContent.id]);

    const gradedCount = testShareData.filter((i) => i.isFullyEvaluated).length;
    const totalCount = testShareData.length;
    const allGraded = totalCount > 0 && gradedCount === totalCount;
    const allShared = totalCount > 0 && testShareData.every((i) => i.sharedInClassroomDate);
    // Legacy tests can only be shared via the copy link, not into the classroom. The record is `{}`
    // while loading (or if it comes back empty), which reads as legacy — gate on the loaded id.
    const canShareInClassroom = !getIsLegacyTest(studentTestContent.id ? studentTestContent : null);
    const shareableResultIds = testShareData
        .filter((i) => i.canBeShared && !i.sharedInClassroomDate)
        .map((i) => i.resultId);
    const shareableCount = shareableResultIds.length;

    const persistShare = async (resultIds: Array<string>): Promise<void> => {
        const now = Date.now();
        const isTarget = (id?: string): id is string => !!id && resultIds.includes(id);

        // Save each target individually so a partial failure doesn't roll back the ones that succeeded.
        const targets = testIndividualResults.filter((r) => isTarget(r.id));
        const outcomes = await Promise.allSettled(
            targets.map((r) => saveStudentTestResult(currentTestId, r.id!, { ...r, sharedInClassroomDate: now }))
        );
        const savedIds = new Set(
            targets.filter((_, index) => outcomes[index].status === "fulfilled").map((r) => r.id!)
        );

        // Commit local state only for results whose save succeeded, so the UI never drifts ahead of the DB.
        setTestIndividualResults(
            testIndividualResults.map((r) => (r.id && savedIds.has(r.id) ? { ...r, sharedInClassroomDate: now } : r))
        );
        setTestShareData(
            testShareData.map((item) => (savedIds.has(item.resultId) ? { ...item, sharedInClassroomDate: now } : item))
        );

        if (savedIds.size < targets.length) {
            throw new Error("Some results could not be shared");
        }
    };

    // Stable references so the memoized StudentResultCard rows don't re-render on every parent state change.
    const triggerLinkCopiedToast = linkCopiedToast.triggerToast;

    const openSinglePopup = useCallback((resultId: string, studentName: string) => {
        setPopupState({ kind: ShareConfirmationKinds.Single, resultId, studentName });
    }, []);

    const openBulkPopup = () => {
        if (shareableCount === 0) return;
        setPopupState({ kind: ShareConfirmationKinds.Bulk });
    };

    const closePopup = () => {
        if (isSharing) return;
        setPopupState({ kind: null });
    };

    const confirmShare = async () => {
        const kind = popupState.kind;
        if (!kind) return;
        const ids =
            kind === ShareConfirmationKinds.Single && popupState.resultId ? [popupState.resultId] : shareableResultIds;
        if (ids.length === 0) {
            setPopupState({ kind: null });
            return;
        }

        setIsSharing(true);
        try {
            await persistShare(ids);
            logEvent("Shared Result In Classroom", {
                testId: studentTestContent.id,
                mode: kind,
                count: ids.length,
            });
            setPopupState({ kind: null });
        } catch (err) {
            console.error("Failed to share results", err);
            shareErrorToast.triggerToast();
        } finally {
            setIsSharing(false);
        }
    };

    const handleGradeStudent = useCallback(
        (resultId: string) => {
            history.push(`/review/${currentTestId}/student/${resultId}`);
        },
        [history, currentTestId]
    );

    const handleGradeOverview = () => {
        history.push(`/review/${currentTestId}`);
    };

    const handleCopyLink = useCallback(
        (link: string) => {
            copy(link);
            triggerLinkCopiedToast();
        },
        [triggerLinkCopiedToast]
    );

    const handleDownload = (format: DownloadFormats) => {
        const dataToExport: ExportRow[] = testShareData.map((d) => ({
            [t_exportName]: d.name,
            [t_exportCorrectPer]: `${d.correctPerc}%`,
            [t_exportIncorrectPer]: `${d.incorrectPerc}%`,
            [t_exportLink]: d.canBeShared ? d.shareLink : t_noLink,
        }));

        try {
            if (format === DownloadFormats.CSV) {
                exportFromJSON({
                    data: dataToExport,
                    fileName: studentTestContent.name,
                    exportType: "csv",
                    withBOM: true,
                });
            } else {
                const worksheet = utils.json_to_sheet(dataToExport);
                const workbook = utils.book_new();
                utils.book_append_sheet(workbook, worksheet, "students");
                writeFile(workbook, (studentTestContent.name || "export") + ".xlsx");
            }
        } catch (err) {
            console.error("Failed to export results", err);
            downloadErrorToast.triggerToast();
            return;
        }

        logEvent("Downloaded Results", { format, testId: studentTestContent.id });
        setIsDownloadPopupOpen(false);
    };

    return (
        <ContentWithHeader
            id={"content-with-header"}
            isFullWidth
            hasFullHeightContent
        >
            {/* flex-1 fills the full-height column the wrapper lays out, so the download bar docks at the
                bottom even when there are only a few students. */}
            <div className="w-full flex-1 flex flex-col bg-background-light">
                <div className="w-full px-s">
                    <BackNavigationButton
                        label={t_backToOverview}
                        onClick={handleGradeOverview}
                    />
                </div>

                <div className={`mx-auto w-full mt-m mb-m ${CONTENT_MAX_WIDTH} px-s flex flex-col gap-m`}>
                    <TestReviewInfoHeader />

                    <ShareStatusBanner
                        gradedCount={gradedCount}
                        totalCount={totalCount}
                        allGraded={allGraded}
                        allShared={allShared}
                        canShareInClassroom={canShareInClassroom}
                        hasShareableTargets={shareableCount > 0}
                        isSharing={isSharing}
                        onGradeClick={handleGradeOverview}
                        onShareAll={openBulkPopup}
                    />

                    <h2 className="text-xl text-p6-grey-dark font-normal">{t_sectionHeader}</h2>

                    <div className="flex flex-col gap-s w-full">
                        {testShareData.map((item) => (
                            <StudentResultCard
                                key={item.resultId}
                                item={item}
                                canShareInClassroom={canShareInClassroom}
                                onShareClick={openSinglePopup}
                                onGradeClick={handleGradeStudent}
                                onCopyLink={handleCopyLink}
                            />
                        ))}
                    </div>
                </div>

                {/* Spacer: fills leftover height on short pages to dock the bar at the bottom; 0 once content fills the viewport. */}
                <div className="flex-grow" />

                {/* Download bar: pinned to the viewport bottom while scrolling (z-50 so cards pass behind it). */}
                <div className="sticky bottom-[0px] z-50 w-[calc(100%_+_2rem)] -mx-s bg-white border-t border-p6-grey-lightest px-m py-s">
                    <div className={`mx-auto ${CONTENT_MAX_WIDTH} flex justify-end`}>
                        <AreaButton
                            variant={ButtonVariant.SECONDARY}
                            role="button"
                            onClick={() => setIsDownloadPopupOpen(true)}
                            phosphorIcon={DownloadSimpleIcon}
                            iconPosition={IconPosition.RIGHT}
                            className={`${ORANGE_OUTLINE_BUTTON_CLASS} ${getStyleBasedOnBreakpoint(
                                Breakpoint.MD,
                                "min-w-[200px]",
                                "w-full"
                            )}`}
                        >
                            {t_download}
                        </AreaButton>
                    </div>
                </div>
            </div>

            <DownloadFormatPopup
                isOpen={isDownloadPopupOpen}
                onClose={() => setIsDownloadPopupOpen(false)}
                onDownloadXlsx={() => handleDownload(DownloadFormats.XLSX)}
                onDownloadCsv={() => handleDownload(DownloadFormats.CSV)}
            />

            <ShareConfirmationPopup
                isOpen={popupState.kind !== null}
                kind={popupState.kind ?? ShareConfirmationKinds.Single}
                studentName={popupState.studentName}
                testName={studentTestContent.name}
                isSharing={isSharing}
                onCancel={closePopup}
                onConfirm={confirmShare}
            />

            <ToastMessage
                isOpen={linkCopiedToast.isOpen}
                onOpenChange={linkCopiedToast.onOpenChange}
                text={t_linkCopied}
            />

            <ToastMessage
                isOpen={shareErrorToast.isOpen}
                onOpenChange={shareErrorToast.onOpenChange}
                text={t_shareFailed}
                variant={ToastVariants.ERROR}
            />

            <ToastMessage
                isOpen={downloadErrorToast.isOpen}
                onOpenChange={downloadErrorToast.onOpenChange}
                text={t_downloadFailed}
                variant={ToastVariants.ERROR}
            />

            <ToastMessage
                isOpen={prepareErrorToast.isOpen}
                onOpenChange={prepareErrorToast.onOpenChange}
                text={t_prepareFailed}
                variant={ToastVariants.ERROR}
            />
        </ContentWithHeader>
    );
};

export default ShareTestReviews;
